import React, { useEffect, useState } from 'react';
import { Card, Switch, Typography, Space, message, InputNumber, Divider, Button, Tag, Tabs, Input, List, Popconfirm } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { 
  GET_ALLOW_TELECONSULTATION, 
  SET_ALLOW_TELECONSULTATION,
  GET_TELECONSULTATION_FEE,
  GET_TELECONSULTATION_FEE_CHINA_LIFE,
  GET_TELECONSULTATION_LEAD_TIME,
  GET_REMOTE_AREA_DELIVERY_EXTRA_FEE,
  GET_BOOKING_DAYS_LIMIT,
  SET_TELECONSULTATION_FEE,
  SET_TELECONSULTATION_LEAD_TIME,
  SET_REMOTE_AREA_DELIVERY_EXTRA_FEE,
  SET_BOOKING_DAYS_LIMIT,
  GET_CLOSE_BUFFER_MINUTES_QUERY,
  SET_CLOSE_BUFFER_MINUTES_MUTATION,
  GET_QUEUE_NUMBER_THRESHOLD_QUERY,
  SET_QUEUE_NUMBER_THRESHOLD_MUTATION,
  GET_APPOINTMENT_ENTRY_BUFFER_MINUTES_QUERY,
  SET_APPOINTMENT_ENTRY_BUFFER_MINUTES_MUTATION,
  GET_DEFAULT_DELIVERY_CONTACT_INFO,
  SET_DEFAULT_DELIVERY_CONTACT_INFO,
  GET_DELIVERY_CONTACT_LIST,
  SET_DELIVERY_CONTACT_LIST
} from '../graphql/queries';
import { usePermissions } from '../hooks/usePermissions';
import { useTranslation } from '../hooks/useTranslation';
import BookingScheduleManager from '../components/BookingScheduleManager';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { canManageSettings } = usePermissions();
  
  // Queries
  const { data: allowData, loading: allowLoading, refetch: refetchAllow } = useQuery(GET_ALLOW_TELECONSULTATION);
  const { data: feeData, loading: feeLoading, refetch: refetchFee } = useQuery(GET_TELECONSULTATION_FEE);
  const { data: chinaLifeFeeData, loading: chinaLifeFeeLoading, refetch: refetchChinaLifeFee } = useQuery(GET_TELECONSULTATION_FEE_CHINA_LIFE);
  const { data: leadTimeData, loading: leadTimeLoading, refetch: refetchLeadTime } = useQuery(GET_TELECONSULTATION_LEAD_TIME);
  const { data: deliveryFeeData, loading: deliveryFeeLoading, refetch: refetchDeliveryFee } = useQuery(GET_REMOTE_AREA_DELIVERY_EXTRA_FEE);
  const { data: bookingDaysData, loading: bookingDaysLoading, refetch: refetchBookingDays } = useQuery(GET_BOOKING_DAYS_LIMIT);
  const { data: closeBufferData, loading: closeBufferLoading, refetch: refetchCloseBuffer } = useQuery(GET_CLOSE_BUFFER_MINUTES_QUERY);
  const { data: queueNumberThresholdData, loading: queueNumberThresholdLoading, refetch: refetchQueueNumberThreshold } = useQuery(GET_QUEUE_NUMBER_THRESHOLD_QUERY);
  const { data: appointmentEntryBufferData, loading: appointmentEntryBufferLoading, refetch: refetchAppointmentEntryBuffer } = useQuery(GET_APPOINTMENT_ENTRY_BUFFER_MINUTES_QUERY);
  const { data: defaultDeliveryContactData, loading: defaultDeliveryContactLoading, refetch: refetchDefaultDeliveryContact } = useQuery(GET_DEFAULT_DELIVERY_CONTACT_INFO);
  const { data: deliveryContactListData, loading: deliveryContactListLoading, refetch: refetchDeliveryContactList } = useQuery(GET_DELIVERY_CONTACT_LIST);
  
  // Mutations
  const [setAllow, { loading: savingAllow }] = useMutation(SET_ALLOW_TELECONSULTATION);
  const [setFee, { loading: savingFee }] = useMutation(SET_TELECONSULTATION_FEE);
  const [setLeadTime, { loading: savingLeadTime }] = useMutation(SET_TELECONSULTATION_LEAD_TIME);
  const [setDeliveryFee, { loading: savingDeliveryFee }] = useMutation(SET_REMOTE_AREA_DELIVERY_EXTRA_FEE);
  const [setBookingDays, { loading: savingBookingDays }] = useMutation(SET_BOOKING_DAYS_LIMIT);
  const [setCloseBuffer, { loading: savingCloseBuffer }] = useMutation(SET_CLOSE_BUFFER_MINUTES_MUTATION);
  const [setQueueNumberThreshold, { loading: savingQueueNumberThreshold }] = useMutation(SET_QUEUE_NUMBER_THRESHOLD_MUTATION);
  const [setAppointmentEntryBuffer, { loading: savingAppointmentEntryBuffer }] = useMutation(SET_APPOINTMENT_ENTRY_BUFFER_MINUTES_MUTATION);
  const [setDefaultDeliveryContactMutation, { loading: savingDefaultDeliveryContact }] = useMutation(SET_DEFAULT_DELIVERY_CONTACT_INFO);
  const [setDeliveryContactListMutation, { loading: savingDeliveryContactList }] = useMutation(SET_DELIVERY_CONTACT_LIST);
  
  // State
  const [enabled, setEnabled] = useState<boolean>(false);
  const [teleconsultationFee, setTeleconsultationFee] = useState<number>(0);
  const [teleconsultationFeeChinaLife, setTeleconsultationFeeChinaLife] = useState<number>(0);
  const [leadTimeHours, setLeadTimeHours] = useState<number>(24);
  const [deliveryExtraFee, setDeliveryExtraFee] = useState<number>(0);
  const [bookingDaysLimit, setBookingDaysLimit] = useState<number>(30);
  const [closeBufferMinutes, setCloseBufferMinutes] = useState<number>(30);
  const [queueNumberThreshold, setQueueNumberThresholdState] = useState<number>(5);
  const [appointmentEntryBufferMinutes, setAppointmentEntryBufferMinutesState] = useState<number>(15);
  const [defaultDeliveryContact, setDefaultDeliveryContactState] = useState<string>('');
  const [defaultDeliveryMobile, setDefaultDeliveryMobile] = useState<string>('');
  const [defaultDeliveryAddress, setDefaultDeliveryAddress] = useState<string>('');
  const [deliveryContactList, setDeliveryContactList] = useState<Array<{ id: string; contact: string; mobile: string; address: string }>>([]);
  const [editingContact, setEditingContact] = useState<{ id: string; contact: string; mobile: string; address: string } | null>(null);
  const [newContact, setNewContact] = useState<{ contact: string; mobile: string; address: string }>({ contact: '', mobile: '', address: '' });

  useEffect(() => {
    if (allowData?.allowTeleconsultation !== undefined) {
      setEnabled(Boolean(allowData.allowTeleconsultation));
    }
  }, [allowData]);

  useEffect(() => {
    if (feeData?.teleconsultationFee !== undefined) {
      setTeleconsultationFee(Number(feeData.teleconsultationFee));
    }
  }, [feeData]);

  useEffect(() => {
    if (chinaLifeFeeData?.teleconsultationFee !== undefined) {
      setTeleconsultationFeeChinaLife(Number(chinaLifeFeeData.teleconsultationFee));
    }
  }, [chinaLifeFeeData]);

  useEffect(() => {
    if (leadTimeData?.teleconsultationLeadTime !== undefined) {
      setLeadTimeHours(Number(leadTimeData.teleconsultationLeadTime));
    }
  }, [leadTimeData]);

  useEffect(() => {
    if (deliveryFeeData?.remoteAreaDeliveryExtraFee !== undefined) {
      setDeliveryExtraFee(Number(deliveryFeeData.remoteAreaDeliveryExtraFee));
    }
  }, [deliveryFeeData]);

  useEffect(() => {
    if (bookingDaysData?.bookingDaysLimit !== undefined) {
      setBookingDaysLimit(Number(bookingDaysData.bookingDaysLimit));
    }
  }, [bookingDaysData]);

  useEffect(() => {
    if (closeBufferData?.closeBufferMinutes !== undefined) {
      setCloseBufferMinutes(Number(closeBufferData.closeBufferMinutes));
    }
  }, [closeBufferData]);

  useEffect(() => {
    if (queueNumberThresholdData?.queueNumberThreshold !== undefined) {
      setQueueNumberThresholdState(Number(queueNumberThresholdData.queueNumberThreshold));
    }
  }, [queueNumberThresholdData]);

  useEffect(() => {
    if (appointmentEntryBufferData?.appointmentEntryBufferMinutes !== undefined) {
      setAppointmentEntryBufferMinutesState(Number(appointmentEntryBufferData.appointmentEntryBufferMinutes));
    }
  }, [appointmentEntryBufferData]);

  useEffect(() => {
    if (defaultDeliveryContactData?.defaultDeliveryContactInfo) {
      setDefaultDeliveryContactState(defaultDeliveryContactData.defaultDeliveryContactInfo.contact || '');
      setDefaultDeliveryMobile(defaultDeliveryContactData.defaultDeliveryContactInfo.mobile || '');
      setDefaultDeliveryAddress(defaultDeliveryContactData.defaultDeliveryContactInfo.address || '');
    }
  }, [defaultDeliveryContactData]);

  useEffect(() => {
    if (deliveryContactListData?.deliveryContactList) {
      setDeliveryContactList(deliveryContactListData.deliveryContactList);
    }
  }, [deliveryContactListData]);

  const onToggle = async (checked: boolean) => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    try {
      await setAllow({ variables: { enabled: checked } });
      setEnabled(checked);
      message.success(t('common.saved'));
      refetchAllow();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
    }
  };

  const onFeeChange = (fee: number | null) => {
    if (fee === null || fee < 0) return;
    setTeleconsultationFee(fee);
  };

  const onFeeBlur = async () => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    
    try {
      await setFee({ variables: { fee: teleconsultationFee, tenant: 'default' } });
      message.success(t('common.saved'));
      refetchFee();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
    }
  };

  const onChinaLifeFeeChange = (fee: number | null) => {
    if (fee === null || fee < 0) return;
    setTeleconsultationFeeChinaLife(fee);
  };

  const onChinaLifeFeeBlur = async () => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }

    try {
      await setFee({
        variables: { fee: teleconsultationFeeChinaLife, tenant: 'china-life' },
      });
      message.success(t('common.saved'));
      refetchChinaLifeFee();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
    }
  };

  const onLeadTimeChange = (hours: number | null) => {
    if (hours === null || hours < 0) return;
    setLeadTimeHours(hours);
  };

  const onLeadTimeBlur = async () => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    
    try {
      await setLeadTime({ variables: { hours: leadTimeHours } });
      message.success(t('common.saved'));
      refetchLeadTime();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
    }
  };

  const onDeliveryFeeChange = (fee: number | null) => {
    if (fee === null || fee < 0) return;
    setDeliveryExtraFee(fee);
  };

  const onDeliveryFeeBlur = async () => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    
    try {
      await setDeliveryFee({ variables: { fee: deliveryExtraFee } });
      message.success(t('common.saved'));
      refetchDeliveryFee();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
    }
  };

  const onBookingDaysChange = (days: number | null) => {
    if (days === null || days < 1) return;
    setBookingDaysLimit(days);
  };

  const onBookingDaysBlur = async () => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    
    try {
      await setBookingDays({ variables: { days: bookingDaysLimit } });
      message.success(t('common.saved'));
      refetchBookingDays();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
    }
  };

  const onCloseBufferChange = (minutes: number | null) => {
    if (minutes === null || minutes < 0) return;
    setCloseBufferMinutes(minutes);
  };

  const onCloseBufferBlur = async () => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    
    try {
      await setCloseBuffer({ variables: { minutes: closeBufferMinutes } });
      message.success(t('common.saved'));
      refetchCloseBuffer();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
    }
  };

  const onQueueNumberThresholdChange = (number: number | null) => {
    if (number === null || number < 1) return;
    setQueueNumberThresholdState(number);
  };

  const onQueueNumberThresholdBlur = async () => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    
    try {
      await setQueueNumberThreshold({ variables: { number: queueNumberThreshold } });
      message.success(t('common.saved'));
      refetchQueueNumberThreshold();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
    }
  };

  const onAppointmentEntryBufferChange = (minutes: number | null) => {
    if (minutes === null || minutes < 0) return;
    setAppointmentEntryBufferMinutesState(minutes);
  };

  const onAppointmentEntryBufferBlur = async () => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    
    try {
      await setAppointmentEntryBuffer({ variables: { minutes: appointmentEntryBufferMinutes } });
      message.success(t('common.saved'));
      refetchAppointmentEntryBuffer();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
    }
  };

  const onDefaultDeliveryContactBlur = async () => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    
    try {
      await setDefaultDeliveryContactMutation({ 
        variables: { 
          contact: defaultDeliveryContact, 
          mobile: defaultDeliveryMobile, 
          address: defaultDeliveryAddress 
        } 
      });
      message.success(t('common.saved'));
      refetchDefaultDeliveryContact();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
    }
  };

  const handleAddContact = async () => {
    if (!newContact.contact || !newContact.mobile || !newContact.address) {
      message.warning('Please fill in all fields');
      return;
    }
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    
    const id = `contact-${Date.now()}`;
    const updatedList = [...deliveryContactList, { ...newContact, id }];
    setDeliveryContactList(updatedList);
    setNewContact({ contact: '', mobile: '', address: '' });
    
    // Auto-save to backend
    try {
      await setDeliveryContactListMutation({ 
        variables: { 
          contacts: updatedList.map(contact => ({
            id: contact.id,
            contact: contact.contact,
            mobile: contact.mobile,
            address: contact.address
          }))
        } 
      });
      message.success(t('common.saved'));
      refetchDeliveryContactList();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
      // Revert on error
      setDeliveryContactList(deliveryContactList);
    }
  };

  const handleEditContact = (contact: { id: string; contact: string; mobile: string; address: string }) => {
    setEditingContact(contact);
  };

  const handleUpdateContact = async () => {
    if (!editingContact) return;
    if (!editingContact.contact || !editingContact.mobile || !editingContact.address) {
      message.warning('Please fill in all fields');
      return;
    }
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    
    const updatedList = deliveryContactList.map(c => c.id === editingContact.id ? editingContact : c);
    setDeliveryContactList(updatedList);
    const contactToSave = editingContact;
    setEditingContact(null);
    
    // Auto-save to backend
    try {
      await setDeliveryContactListMutation({ 
        variables: { 
          contacts: updatedList.map(contact => ({
            id: contact.id,
            contact: contact.contact,
            mobile: contact.mobile,
            address: contact.address
          }))
        } 
      });
      message.success(t('common.saved'));
      refetchDeliveryContactList();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
      // Revert on error
      setDeliveryContactList(deliveryContactList);
      setEditingContact(contactToSave);
    }
  };

  const handleDeleteContact = async (id: string) => {
    if (!canManageSettings()) {
      message.error(t('common.noPermission'));
      return;
    }
    
    const updatedList = deliveryContactList.filter(c => c.id !== id);
    const previousList = deliveryContactList;
    setDeliveryContactList(updatedList);
    
    // Auto-save to backend
    try {
      await setDeliveryContactListMutation({ 
        variables: { 
          contacts: updatedList.map(contact => ({
            id: contact.id,
            contact: contact.contact,
            mobile: contact.mobile,
            address: contact.address
          }))
        } 
      });
      message.success(t('common.saved'));
      refetchDeliveryContactList();
    } catch (e: any) {
      message.error(e.message || 'Failed to save');
      // Revert on error
      setDeliveryContactList(previousList);
    }
  };

  const isLoading = allowLoading || feeLoading || chinaLifeFeeLoading || leadTimeLoading || deliveryFeeLoading || bookingDaysLoading || closeBufferLoading || queueNumberThresholdLoading || appointmentEntryBufferLoading || defaultDeliveryContactLoading || deliveryContactListLoading;
  const isSaving = savingAllow || savingFee || savingLeadTime || savingDeliveryFee || savingBookingDays || savingCloseBuffer || savingQueueNumberThreshold || savingAppointmentEntryBuffer || savingDefaultDeliveryContact || savingDeliveryContactList;

  return (
    <Card loading={isLoading}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Title level={3}>{t('common.settings')}</Title>
        
        <Tabs defaultActiveKey="general" size="large">
          <TabPane tab="General Settings" key="general">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Teleconsultation Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{t('settings.allowTeleconsultationTitle') || 'Allow Teleconsultation'}</div>
                  <Text type="secondary">
                    {t('settings.allowTeleconsultationDesc') || 'When off, all inbound teleconsultations from the public app are blocked.'}
                  </Text>
                </div>
                <Switch checked={enabled} onChange={onToggle} loading={savingAllow} />
              </div>

              <Divider />

              {/* Teleconsultation Fee */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{t('settings.teleconsultationFeeTitle') || 'Teleconsultation Fee (HKD)'}</div>
                  <Text type="secondary">
                    {t('settings.teleconsultationFeeDesc') || 'Base consultation fee for teleconsultation appointments.'}
                  </Text>
                </div>
                <InputNumber
                  min={0}
                  step={10}
                  value={teleconsultationFee}
                  onChange={onFeeChange}
                  onBlur={onFeeBlur}
                  style={{ width: 180 }}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                />
              </div>

              <Divider />

              {/* China Life Telemedicine Fee */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{t('settings.telemedicineFeeChinaLifeTitle')}</div>
                  <Text type="secondary">
                    {t('settings.telemedicineFeeChinaLifeDesc')}
                  </Text>
                </div>
                <InputNumber
                  min={0}
                  step={10}
                  value={teleconsultationFeeChinaLife}
                  onChange={onChinaLifeFeeChange}
                  onBlur={onChinaLifeFeeBlur}
                  style={{ width: 180 }}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                />
              </div>


              {/* Remote Area Delivery Extra Fee */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{t('settings.remoteAreaDeliveryFeeTitle') || 'Remote Area Delivery Extra Fee (HKD)'}</div>
                  <Text type="secondary">
                    {t('settings.remoteAreaDeliveryFeeDesc') || 'Additional delivery fee for remote area addresses.'}
                  </Text>
                </div>
                <InputNumber
                  min={0}
                  step={10}
                  value={deliveryExtraFee}
                  onChange={onDeliveryFeeChange}
                  onBlur={onDeliveryFeeBlur}
                  style={{ width: 180 }}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                />
              </div>
              
              {/* Lead Time */}
              {/* <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{t('settings.leadTimeTitle') || 'Minimum Lead Time (Hours)'}</div>
                  <Text type="secondary">
                    {t('settings.leadTimeDesc') || 'Minimum time required in advance for teleconsultation bookings.'}
                  </Text>
                </div>
                <InputNumber
                  min={0}
                  max={168}
                  step={1}
                  value={leadTimeHours}
                  onChange={onLeadTimeChange}
                  onBlur={onLeadTimeBlur}
                  style={{ width: 180 }}
                  formatter={value => `${value} hours`}
                  parser={value => Number(value!.replace(/\s*hours?/i, ''))}
                />
              </div> */}

              {/* Close Buffer Minutes */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{t('settings.closeBufferMinutesTitle') || 'Close Buffer Minutes'}</div>
                  <Text type="secondary">
                    {t('settings.closeBufferMinutesDesc') || 'Stop queuing this many minutes before closing hours to ensure smooth operations.'}
                  </Text>
                </div>
                <InputNumber
                  min={0}
                  max={120}
                  step={5}
                  value={closeBufferMinutes}
                  onChange={onCloseBufferChange}
                  onBlur={onCloseBufferBlur}
                  style={{ width: 180 }}
                  formatter={value => `${value} minutes`}
                  parser={value => Number(value!.replace(/\s*minutes?/i, ''))}
                />
              </div>

                {/* Booking Days Limit */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{t('settings.bookingDaysLimitTitle') || 'Booking Days Limit'}</div>
                  <Text type="secondary">
                    {t('settings.bookingDaysLimitDesc') || 'Maximum number of days in the future allowed for booking appointments.'}
                  </Text>
                </div>
                <InputNumber
                  min={1}
                  max={365}
                  step={1}
                  value={bookingDaysLimit}
                  onChange={onBookingDaysChange}
                  onBlur={onBookingDaysBlur}
                  style={{ width: 180 }}
                  formatter={value => `${value} days`}
                  parser={value => Number(value!.replace(/\s*days?/i, ''))}
                />
              </div>

              <Divider />

              {/* Queue Number Threshold */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{t('settings.queueNumberThresholdTitle') || 'Queue Number Threshold'}</div>
                  <Text type="secondary">
                    {t('settings.queueNumberThresholdDesc') || 'Maximum queue number that allows entering consultation room. Patients with queue numbers below this threshold can enter.'}
                  </Text>
                </div>
                <InputNumber
                  min={1}
                  max={100}
                  step={1}
                  value={queueNumberThreshold}
                  onChange={onQueueNumberThresholdChange}
                  onBlur={onQueueNumberThresholdBlur}
                  style={{ width: 180 }}
                  formatter={value => `#${value}`}
                  parser={value => Number(value!.replace(/#/g, ''))}
                />
              </div>

              {/* Appointment Entry Buffer Minutes */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{t('settings.appointmentEntryBufferMinutesTitle') || 'Appointment Entry Buffer Minutes'}</div>
                  <Text type="secondary">
                    {t('settings.appointmentEntryBufferMinutesDesc') || 'Minutes before appointment time that allows entering consultation room. Patients can enter this many minutes before their scheduled appointment.'}
                  </Text>
                </div>
                <InputNumber
                  min={0}
                  max={60}
                  step={5}
                  value={appointmentEntryBufferMinutes}
                  onChange={onAppointmentEntryBufferChange}
                  onBlur={onAppointmentEntryBufferBlur}
                  style={{ width: 180 }}
                  formatter={value => `${value} minutes`}
                  parser={value => Number(value!.replace(/\s*minutes?/i, ''))}
                />
              </div>

              <Divider />

              {/* Default Delivery Contact Info */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{t('settings.defaultDeliveryContactTitle') || 'Default Delivery Contact Info'}</div>
                  <Text type="secondary">
                    {t('settings.defaultDeliveryContactDesc') || 'Default contact information to be used for delivery appointments.'}
                  </Text>
                </div>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input
                    placeholder="Contact Name"
                    value={defaultDeliveryContact}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDefaultDeliveryContactState(e.target.value)}
                    onBlur={onDefaultDeliveryContactBlur}
                    style={{ width: '100%' }}
                  />
                  <Input
                    placeholder="Mobile Number"
                    value={defaultDeliveryMobile}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDefaultDeliveryMobile(e.target.value)}
                    onBlur={onDefaultDeliveryContactBlur}
                    style={{ width: '100%' }}
                  />
                  <Input.TextArea
                    placeholder="Address"
                    value={defaultDeliveryAddress}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDefaultDeliveryAddress(e.target.value)}
                    onBlur={onDefaultDeliveryContactBlur}
                    style={{ width: '100%' }}
                    rows={3}
                  />
                </Space>
              </div>

              <Divider />

              {/* Delivery Contact List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{t('settings.deliveryContactListTitle') || 'Delivery Contact List'}</div>
                  <Text type="secondary">
                    {t('settings.deliveryContactListDesc') || 'Manage a list of delivery contact information for delivery appointments.'}
                  </Text>
                </div>
                
                {/* Add New Contact */}
                <Card size="small" title={t('settings.addNewContact') || 'Add New Contact'}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                      placeholder="Contact Name"
                      value={newContact.contact}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewContact({ ...newContact, contact: e.target.value })}
                      style={{ width: '100%' }}
                    />
                    <Input
                      placeholder="Mobile Number"
                      value={newContact.mobile}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewContact({ ...newContact, mobile: e.target.value })}
                      style={{ width: '100%' }}
                    />
                    <Input.TextArea
                      placeholder="Address"
                      value={newContact.address}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewContact({ ...newContact, address: e.target.value })}
                      style={{ width: '100%' }}
                      rows={3}
                    />
                    <Button type="primary" onClick={handleAddContact} style={{ width: '100%' }}>
                      {t('common.add') || 'Add Contact'}
                    </Button>
                  </Space>
                </Card>

                {/* Contact List */}
                <List
                  dataSource={deliveryContactList}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button
                          key="edit"
                          type="link"
                          onClick={() => handleEditContact(item)}
                        >
                          {t('common.edit') || 'Edit'}
                        </Button>,
                        <Popconfirm
                          key="delete"
                          title={t('common.confirmDelete') || 'Are you sure you want to delete this contact?'}
                          onConfirm={() => handleDeleteContact(item.id)}
                          okText={t('common.yes') || 'Yes'}
                          cancelText={t('common.no') || 'No'}
                        >
                          <Button type="link" danger>
                            {t('common.delete') || 'Delete'}
                          </Button>
                        </Popconfirm>
                      ]}
                    >
                      <List.Item.Meta
                        title={item.contact}
                        description={
                          <div>
                            <div>{item.mobile}</div>
                            <div>{item.address}</div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />

                {/* Edit Contact Modal */}
                {editingContact && (
                  <Card size="small" title={t('settings.editContact') || 'Edit Contact'}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Input
                        placeholder="Contact Name"
                        value={editingContact.contact}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingContact({ ...editingContact, contact: e.target.value })}
                        style={{ width: '100%' }}
                      />
                      <Input
                        placeholder="Mobile Number"
                        value={editingContact.mobile}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingContact({ ...editingContact, mobile: e.target.value })}
                        style={{ width: '100%' }}
                      />
                      <Input.TextArea
                        placeholder="Address"
                        value={editingContact.address}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditingContact({ ...editingContact, address: e.target.value })}
                        style={{ width: '100%' }}
                        rows={3}
                      />
                      <Space>
                        <Button type="primary" onClick={handleUpdateContact}>
                          {t('common.save') || 'Save'}
                        </Button>
                        <Button onClick={() => setEditingContact(null)}>
                          {t('common.cancel') || 'Cancel'}
                        </Button>
                      </Space>
                    </Space>
                  </Card>
                )}

              </div>
            </Space>
          </TabPane>

          <TabPane tab="Booking Schedules" key="schedules">
            <BookingScheduleManager />
          </TabPane>
        </Tabs>
      </Space>
    </Card>
  );
};

export default Settings; 